import React, { useRef } from "react";
import useLayout from "~/hooks/useLayout";
import useMount from "~/hooks/useMount";

import companyImage from "~/images/company.svg";
import chatBro from "~/images/chat_bro.svg";
import {
  AppleLogo,
  Coin,
  EnvelopeSimple,
  GooglePlayLogo,
  MapPin,
  ShieldCheck,
  UsersThree,
} from "phosphor-react";
import ReasonItem from "~/landingPage/pages/companiesPage/components/ReasonItem";
import PriceCard from "~/landingPage/pages/companiesPage/components/PriceCard";
import CustomModal from "~/landingPage/components/CustomModal";
import DownloadAppButton from "~/landingPage/components/buttons/DownloadAppButton";
import {
  COMPANY_PREMIUM_ANNUALLY,
  COMPANY_PREMIUM_MONTHLY,
  COMPANY_PREMIUM_SIX_MONTHS,
  CONTACT_EMAIL,
} from "~/utils/Constants";

export default function CompaniesPage() {
  const { setLayout } = useLayout();

  useMount(() => {
    setLayout({
      title: "Entreprises",
      activeMenuKeys: ["companies"],
    });
  });

  const subscribeModal = useRef();
  const downloadModal = useRef();

  const handleContactClick = () => {
    const email = CONTACT_EMAIL;
    const subject = `Négociation offre premium (entreprise)`;

    window.location.href = `mailto:${email}?subject=${encodeURIComponent(
      subject
    )}`;
  };

  return (
    <>
      <section className="flex lg:flex-row flex-col justify-center items-center lg:mt-0 mt-8">
        <div className="md:w-[48] flex flex-col justify-center lg:pl-20 px-4 gap-y-10">
          <div className="font-bold text-4xl">
            Boostez votre{" "}
            <span className="text-primary">visibilité&nbsp;!</span>
          </div>
          <div>
            <div className="text-2xl">
              Entrez en contact avec les micro-influenceurs qui vous
              correspondent et promouvez votre activité !
            </div>
            <div className="flex md:flex-row flex-col md:space-x-10 md:space-y-0 space-y-4 justify-between md:justify-center md:items-center w-fit mt-4">
              <DownloadAppButton
                logo={<AppleLogo size="22" />}
                text={"Depuis l'App Store"}
                onclick={() => downloadModal.current?.open()}
              ></DownloadAppButton>
              <DownloadAppButton
                logo={<GooglePlayLogo size="22" />}
                text={"Depuis Google Play"}
                onclick={() => downloadModal.current?.open()}
              ></DownloadAppButton>
            </div>
          </div>
        </div>
        <div className="xl:w-[35%] w-[80%] h-[90%] flex items-center lg:justify-end justify-center px-8 mt-8 lg:mt-0">
          <img
            src={companyImage}
            alt="présentation entreprise"
            className="max-w-[95%]"
          />
        </div>
      </section>
      <section className="w-full flex lg:flex-row flex-col-reverse justify-center items-center">
        <div className="xl:w-[35%] w-[80%] h-[90%] flex items-center lg:justify-end justify-center px-8">
          <img src={chatBro} alt="influence" />
        </div>
        <div className="lg:w-[48%] h-full flex flex-col justify-center gap-y-10 px-4">
          <div className="font-bold text-3xl lg:mt-0 mt-12">
            La micro-influence, c'est mieux&nbsp;!
          </div>
          <div className="md:text-xl text-md">
            Sur les réseaux sociaux, elle touche les consommateurs de manière
            authentique et significative. <br />
            <br />
            Des individus passionnés, avec une influence ciblée et un taux
            d'engagement élevé, génèrent un engagement réel. Leur crédibilité et
            confiance favorisent les décisions d'achat. <br />
            <br />
            Avec la micro-influence, établissez une stratégie de promotion
            efficace et locale !
          </div>
        </div>
      </section>
      <section className="bg-gray-100 flex flex-col justify-center items-center pt-6 pb-10">
        <div className="w-[90%]">
          <div className="font-bold text-3xl text-center mb-20 mt-10">
            En tant qu'entreprise, pourquoi choisir{" "}
            <span className="text-primary">Influx&nbsp;?</span>
          </div>
          <div className="flex flex-col">
            <div className="flex md:flex-row flex-col justify-around items-center md:mb-8">
              <ReasonItem
                icon={<Coin />}
                text={
                  "Aucune commission, faites autant de partenariats et placements de produit que vous voulez !"
                }
              />
              <ReasonItem
                icon={<MapPin />}
                text={
                  "Des influenceurs près de votre lieu d'activité pour toucher une audience proche de vous !"
                }
              />
            </div>
            <div className="flex md:flex-row flex-col justify-around items-center">
              <ReasonItem
                icon={<ShieldCheck />}
                text={
                  "Des influenceurs tous vérifiés humainement. Vous ne tomberez jamais sur un faux compte !"
                }
              />
              <ReasonItem
                icon={<UsersThree />}
                text={
                  "Une équipe disponible et réactive pour répondre à toutes vos questions !"
                }
              />
            </div>
          </div>
        </div>
      </section>
      <section className="my-20 px-4">
        <h2 className="font-bold text-3xl text-center">
          Choisissez l'offre qui vous convient le mieux.
        </h2>
        <p className="text-center">
          Toutes les fonctionnalités de l'application ainsi qu'un support
          disponible 7j/7.
        </p>
        <div className="flex justify-center mt-6 lg:flex-row flex-col items-center">
          <PriceCard
            topText="Abonnement mensuel - sans engagement"
            price={COMPANY_PREMIUM_MONTHLY}
            isBestOffer={false}
            frequency={"mois"}
            onClick={() => subscribeModal.current?.open()}
          />
          <PriceCard
            topText="Abonnement semestriel - sans engagement"
            price={COMPANY_PREMIUM_SIX_MONTHS}
            isBestOffer={false}
            frequency={"6 mois"}
            newPriceText={"Soit 38€/mois au lieu de 49€."}
            onClick={() => subscribeModal.current?.open()}
          />
          <PriceCard
            topText="Abonnement annuel - sans engagement"
            price={COMPANY_PREMIUM_ANNUALLY}
            isBestOffer={true}
            frequency={"an"}
            newPriceText={"Soit 29€/mois au lieu de 49€."}
            onClick={() => subscribeModal.current?.open()}
          />
        </div>
        <h3 className="font-bold text-xl text-center mt-12">
          Réduction possible des tarifs en nous contactant !
        </h3>
        <p className="text-center mb-8">
          Négociez une offre plus avantageuse directement avec nous.
        </p>
        <div
          className="shadow-xl py-6 px-10 m-auto flex flex-col justify-center items-center cursor-pointer border-2 rounded w-72"
          onClick={handleContactClick}
        >
          <EnvelopeSimple size={55} />
          <div className="bg-primary py-1 px-2 rounded-xl mt-2">
            <p className="text-white">{CONTACT_EMAIL}</p>
          </div>
        </div>
      </section>
      <CustomModal
        ref={subscribeModal}
        header="Inscription"
        body="L'inscription sera bientôt disponible sur le site web. En attendant, inscrivez-vous directement sur l'application mobile et choisissez l'offre qui vous convient !"
      />
      <CustomModal
        ref={downloadModal}
        header="Téléchargement"
        body={
          "L'application mobile sera très prochainement disponible sur Google Play et l'App Store, merci de patienter encore un peu. N'hésitez pas à nous contacter pour plus d'informations !"
        }
      />
    </>
  );
}
